/**
 * How a phone list ends — `docs/phone-list-loading.md`, from
 * `29-phone-list-loading/html/phone-list-loading.html`.
 *
 * A phone's bottom edge can hold one anchored thing. On a standalone list
 * that was the compact pager; on a record page it is the tab bar, so the
 * pager ended up loose above it — Aaron's screenshot of "1–44 of 44" with two
 * dead arrows floating over the Jobs tab. Pagination is a desktop idea
 * anyway: nobody thinks in pages of fifty on a 390px screen.
 *
 * So the list loads as you scroll, and this is the thing at the bottom that
 * says what is happening. Four states, in order: a page on its way, a page
 * waiting to be asked for, a list that has all of itself, and a list that was
 * never longer than one page — which ends with nothing at all, because a rule
 * under forty rows announcing that there are forty rows is furniture.
 */
import { useEffect, useRef, type ReactElement } from 'react'
import { cn } from '../lib/cn.js'
import { formatNumber } from '../lib/format.js'
import { IconChevronDown, IconSpinner } from '../frame/icons.js'

export interface ListEndProps {
  /** Every row the question matches, not just the ones fetched. */
  total: number
  /** How many are on screen. */
  shown: number
  hasMore: boolean
  loadingMore: boolean
  /** The last page failed on its way in. */
  failed?: boolean
  onMore: () => void
  /** What the rows are — "jobs", "contacts". Lower case, plural. */
  noun: string
  /** The page size, so a list that never had a second page says nothing. */
  pageSize?: number
}

export function ListEnd({
  total, shown, hasMore, loadingMore, failed = false, onMore, noun, pageSize = 50,
}: ListEndProps): ReactElement | null {
  const sentinel = useRef<HTMLDivElement>(null)

  /*
   * One screen early, so the next fifty are usually there before the reader
   * reaches the last card and the list feels like it has no end rather than
   * like it keeps stopping.
   *
   * `IntersectionObserver` is missing in jsdom and in some old WebViews. That
   * is not a failure case to handle — the button below does the same thing,
   * and it is the *reason* the button exists rather than a spinner alone.
   */
  useEffect(() => {
    const node = sentinel.current
    if (!node || typeof IntersectionObserver === 'undefined') return undefined
    if (!hasMore || loadingMore || failed) return undefined

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) onMore()
    }, { rootMargin: '0px 0px 100% 0px' })

    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, loadingMore, failed, onMore])

  if (loadingMore) {
    return (
      <div className={cn('mx-4 mb-2 mt-1 flex h-12 shrink-0 items-center justify-center gap-2',
        'text-cell text-text-muted')}>
        <span className="text-accent"><IconSpinner size={16} /></span>
        Loading {formatNumber(pageSize)} more…
      </div>
    )
  }

  if (hasMore) {
    const left = Math.max(0, total - shown)
    return (
      <>
        <div ref={sentinel} aria-hidden className="h-px shrink-0" />
        <button
          type="button"
          onClick={onMore}
          className={cn(
            /*
             * 48 and `shrink-0`, and the second matters as much as the first:
             * this sits in a flex column, and a flex item with a fixed height
             * and no content-based minimum is squeezed thinner the moment the
             * column overflows. That is the inconsistency the mockup's first
             * pass showed.
             */
            'mx-4 mb-2 mt-1 box-border flex h-12 min-h-12 shrink-0 items-center',
            'justify-center gap-2 self-stretch rounded-card border border-line bg-surface',
            'text-[14px] font-semibold text-accent',
          )}
        >
          <IconChevronDown size={14} />
          {failed ? 'Try again' : `Show ${formatNumber(pageSize)} more`}
          {!failed && (
            <span className="text-[12px] font-medium text-text-faint">
              · {formatNumber(left)} left
            </span>
          )}
        </button>
        <div className="mx-4 mb-2 shrink-0 text-center text-[11.5px] text-text-faint">
          {formatNumber(shown)} of {formatNumber(total)} shown
        </div>
      </>
    )
  }

  /* Everything is here. A list that was never longer than a page says so by
     saying nothing — there was never a question about whether more existed. */
  if (total <= pageSize) return null

  return (
    <div className={cn('mx-4 my-1.5 flex shrink-0 items-center gap-2.5',
      'text-[11.5px] text-text-faint')}>
      <span aria-hidden className="h-px flex-1 bg-line" />
      all {formatNumber(total)} {noun}
      <span aria-hidden className="h-px flex-1 bg-line" />
    </div>
  )
}
